import { CalendarClock, User, Users, MapPin, BarChart3 } from 'lucide-react';
import { theme, rgba } from '../theme';

// Home page below manager (2026-10-05) - replaces the Executive Dashboard for employees
// and the analyst. Renders only what GET /api/business-data returned for this user, which
// the backend already scoped (services/accessScope.js):
//   view 'self_site': their own record (with their own pay), their site(s), the people at
//                     those sites (name and title only) and the shifts there.
//   view 'totals':    company-wide aggregates only (the analyst account).
// Nothing here filters data itself; it can't show more than the server sent.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString()}`);

function upcoming(shifts, today) {
  return (shifts || [])
    .filter(s => s.status !== 'cancelled' && s.date >= today)
    .sort((a, b) => (a.date + (a.startTime || '')).localeCompare(b.date + (b.startTime || '')));
}

export default function MyHomePage({ businessData, isLoading, error, userName, onNavigate }) {
  const view = businessData?.view;
  const today = new Date().toISOString().slice(0, 10);

  if (isLoading) return <div style={styles.page}><p style={styles.muted}>Loading...</p></div>;
  if (error) return <div style={styles.page}><div style={styles.errorBox}>{error}</div></div>;

  if (view === 'totals') {
    const t = businessData.totals || {};
    const tiles = [
      ['Monthly revenue', money(t.monthlyRevenue)],
      ['Gross profit', money(t.grossProfit)],
      ['Profit margin', t.profitMargin == null ? '—' : `${t.profitMargin}%`],
      ['Net profit', money(t.netProfit)],
      ['Employees', t.headcount == null ? '—' : `${t.activeHeadcount} active of ${t.headcount}`],
      ['Clients', t.clientCount ?? '—'],
    ];
    return (
      <div style={styles.page}>
        <h1 style={styles.title}><BarChart3 size={24} style={styles.icon} />Company totals</h1>
        <p style={styles.sub}>Company-wide figures only. Individual employee and client records aren't shown to this account.</p>
        <div style={styles.grid}>
          {tiles.map(([label, value]) => (
            <div key={label} style={styles.card}>
              <div style={styles.label}>{label}</div>
              <div style={styles.big}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const self = businessData?.self || null;
  const clients = businessData?.clients || [];
  const people = (businessData?.employees || []).filter(e => !self || e.id !== self.id);
  const siteName = (id) => clients.find(c => c.id === id)?.name || 'Unknown site';
  const personName = (id) => (self && id === self.id ? 'You' : people.find(e => e.id === id)?.name || 'Someone');
  const mine = upcoming((businessData?.shifts || []).filter(s => self && s.employeeId === self.id), today);
  const atSite = upcoming((businessData?.shifts || []).filter(s => !self || s.employeeId !== self.id), today);

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Welcome{userName ? `, ${userName}` : ''}</h1>
      {!self && (
        <p style={styles.sub}>Your account isn't linked to an employee record yet, so there's no schedule or profile to show. Ask your manager to link it.</p>
      )}

      {self && (
        <div style={styles.grid}>
          <div style={styles.card}>
            <h2 style={styles.h2}><CalendarClock size={18} style={styles.icon} />My schedule</h2>
            {mine.length === 0 && <p style={styles.muted}>No upcoming shifts on file.</p>}
            {mine.slice(0, 8).map(s => (
              <div key={s.id} style={styles.row}>
                <span>{s.date}</span><span>{s.startTime}–{s.endTime}</span><span>{siteName(s.clientId)}</span>
              </div>
            ))}
            <button style={styles.linkBtn} onClick={() => onNavigate && onNavigate('schedule')}>Open full schedule</button>
          </div>

          <div style={styles.card}>
            <h2 style={styles.h2}><User size={18} style={styles.icon} />My profile</h2>
            <div style={styles.row}><span style={styles.label}>Name</span><span>{self.name}</span></div>
            <div style={styles.row}><span style={styles.label}>Title</span><span>{self.role || '—'}</span></div>
            <div style={styles.row}><span style={styles.label}>Status</span><span>{self.status || '—'}</span></div>
            <div style={styles.row}><span style={styles.label}>Pay</span><span>{self.hourlyRate == null ? '—' : `${money(self.hourlyRate)}/hr`}</span></div>
            {self.email && <div style={styles.row}><span style={styles.label}>Email</span><span>{self.email}</span></div>}
            {self.phone && <div style={styles.row}><span style={styles.label}>Phone</span><span>{self.phone}</span></div>}
          </div>

          <div style={styles.card}>
            <h2 style={styles.h2}><MapPin size={18} style={styles.icon} />My site{clients.length === 1 ? '' : 's'}</h2>
            {clients.length === 0 && <p style={styles.muted}>You aren't assigned to a site right now.</p>}
            {clients.map(c => <div key={c.id} style={styles.row}><span>{c.name}</span></div>)}
            {people.length > 0 && (
              <>
                <h3 style={styles.h3}><Users size={16} style={styles.icon} />Working there</h3>
                {people.map(p => <div key={p.id} style={styles.row}><span>{p.name}</span><span style={styles.muted}>{p.role || ''}</span></div>)}
              </>
            )}
            {atSite.length > 0 && (
              <>
                <h3 style={styles.h3}>Upcoming shifts at your site{clients.length === 1 ? '' : 's'}</h3>
                {atSite.slice(0, 8).map(s => (
                  <div key={s.id} style={styles.row}>
                    <span>{s.date} {s.startTime}–{s.endTime}</span><span>{personName(s.employeeId)}</span><span style={styles.muted}>{siteName(s.clientId)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page:  { padding: '32px', minHeight: '100vh', background: `linear-gradient(135deg,${rgba(theme.colors.primaryRgb, 0.05)},${rgba(theme.colors.accentRgb, 0.05)})` },
  title: { color: '#e2e8f0', fontSize: '28px', fontWeight: '700', margin: '0 0 8px', display: 'flex', alignItems: 'center' },
  sub:   { color: theme.colors.textOnPageBackground, fontSize: '14px', marginBottom: '24px' },
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 16 },
  card:  { background: 'rgba(15,23,42,0.6)', border: `1px solid ${rgba(theme.colors.primaryRgb, 0.2)}`, borderRadius: 12, padding: 20, color: '#e2e8f0' },
  h2:    { fontSize: 16, fontWeight: 700, margin: '0 0 12px', display: 'flex', alignItems: 'center' },
  h3:    { fontSize: 13, fontWeight: 700, margin: '16px 0 8px', display: 'flex', alignItems: 'center', color: '#cbd5e1' },
  row:   { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid rgba(148,163,184,0.1)', fontSize: 13 },
  label: { color: '#94a3b8', fontSize: 12 },
  big:   { fontSize: 24, fontWeight: 700, marginTop: 6 },
  muted: { color: '#94a3b8', fontSize: 13 },
  icon:  { marginRight: 8, verticalAlign: 'middle' },
  linkBtn: { marginTop: 12, background: 'transparent', border: `1px solid ${rgba(theme.colors.primaryRgb, 0.5)}`, color: theme.colors.primary, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 },
  errorBox: { padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171' },
};
